import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  getActiveAnnouncementBanners,
  getLocalizedBanners,
} from "../../../firebase/announcement-functions";
import { DEFAULT_STORE_ID } from "../../../firebase/firebase";
import { useLanguage } from "../../../context/LanguageContext";
import "./AnnouncementBanner.css";

const AnnouncementBanner = ({
  position = "top",
  storeId = DEFAULT_STORE_ID,
}) => {
  // Store RAW data (before translation)
  const [rawBanners, setRawBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [closedBanners, setClosedBanners] = useState(new Set());
  const [isVisible, setIsVisible] = useState(true);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [isHiddenByScroll, setIsHiddenByScroll] = useState(false);
  const autoSlideRef = useRef(null);
  const lastScrollY = useRef(0);
  const { language } = useLanguage();

  // Hide banner on scroll down, show on scroll up
  useEffect(() => {
    const handleScroll = () => {
      // Try to get scroll from layout container first, fallback to window
      const layoutContainer = document.querySelector('.main-layout');
      const currentScrollY = layoutContainer ? layoutContainer.scrollTop : window.scrollY;
      
      if (currentScrollY > lastScrollY.current && currentScrollY > 50) {
        // Scrolling down & past threshold
        setIsHiddenByScroll(true);
      } else if (currentScrollY < lastScrollY.current || currentScrollY <= 50) {
        // Scrolling up or at top
        setIsHiddenByScroll(false);
      }
      
      lastScrollY.current = currentScrollY;
    };

    // Listen to both layout container and window for scroll events
    const layoutContainer = document.querySelector('.main-layout');
    if (layoutContainer) {
      layoutContainer.addEventListener('scroll', handleScroll, { passive: true });
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      if (layoutContainer) {
        layoutContainer.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // 1. Fetch Data ONCE (depend only on storeId)
  useEffect(() => {
    const loadBanners = async () => {
      try {
        setLoading(true);
        // Use the cached fetcher we created
        const bannersData = await getActiveAnnouncementBanners(storeId);
        setRawBanners(bannersData);
        if (bannersData.length > 0) {
          setCurrentIndex(0);
        }
      } catch (error) {
        console.error("Error loading announcement banners:", error);
        setRawBanners([]);
      } finally {
        setLoading(false);
      }
    };

    if (storeId) {
      loadBanners();
    }
  }, [storeId]); // ⚡ Removed 'language' dependency

  const visibleBanners = useMemo(() => {
    // If no real banners, show a test banner for positioning
    if (!rawBanners.length) {
      return [];
    }

    // Translate
    const localized = getLocalizedBanners(rawBanners, language);

    // Filter closed
    return localized.filter((banner) => !closedBanners.has(banner.id));
  }, [rawBanners, language, closedBanners]); // ✅ Added closing brackets

  // Set hasAnimated to true after animation completes
  useEffect(() => {
    if (!loading && visibleBanners.length > 0 && !hasAnimated) {
      const timer = setTimeout(() => setHasAnimated(true), 1000); // 1000ms matches animation duration
      return () => clearTimeout(timer);
    }
  }, [loading, visibleBanners.length, hasAnimated]);

  // Auto-slide functionality
  useEffect(() => {
    if (visibleBanners.length > 1 && isVisible) {
      autoSlideRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % visibleBanners.length);
      }, 4000);

      return () => {
        if (autoSlideRef.current) clearInterval(autoSlideRef.current);
      };
    }
  }, [visibleBanners.length, isVisible]);

  const handlePrevious = () => {
    if (visibleBanners.length > 1) {
      setCurrentIndex((prev) =>
        prev === 0 ? visibleBanners.length - 1 : prev - 1
      );
    }
  };

  const handleNext = () => {
    if (visibleBanners.length > 1) {
      setCurrentIndex((prev) => (prev + 1) % visibleBanners.length);
    }
  };

  const handleClose = (bannerId) => {
    setClosedBanners((prev) => new Set(prev).add(bannerId));
  };

  // Don't render if loading, no banners, or component is hidden
  if (loading || visibleBanners.length === 0 || !isVisible) {
    return null;
  }

  const currentBanner = visibleBanners[currentIndex];
  const showNavigation = visibleBanners.length > 1;

  return (
    <div className={`announcement-banner-container ${position} ${isHiddenByScroll ? 'hidden-by-scroll' : ''}`}>
      <div className="announcement-banner">
        {/* Navigation arrows */}
        {showNavigation && (
          <>
            <button
              className="nav-arrow nav-arrow-left"
              onClick={handlePrevious}
            >
              ‹
            </button>
            <button className="nav-arrow nav-arrow-right" onClick={handleNext}>
              ›
            </button>
          </>
        )}

        {/* Banner content */}
        <div className="banner-content">
          {currentBanner.linkUrl ? (
            <a
              href={currentBanner.linkUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {currentBanner.text}
            </a>
          ) : (
            <span>{currentBanner.text}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnnouncementBanner;
